import { useMemo, useState } from 'react';
import type { CentroArvoreNo, CentroNoResponse } from '@triade/contracts';
import { useMoverCentro } from '@/api/centros-custo';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { alvosDeMover, rotuloCentro } from './arvore-logica';
import { mensagemCentro } from './mensagens';

const RAIZ = '__raiz__';

export interface MoverCentroProps {
  centro: CentroNoResponse;
  arvore: CentroArvoreNo[];
  onFechar: () => void;
  onMovido: (centro: CentroNoResponse) => void;
}

/**
 * "Mover para…": novo pai = um sintetico ATIVO da arvore (ou a raiz), sem o proprio centro, sem descendentes e sem o pai atual.
 * Analitico nao e destino. Ciclo, pai inativo e identificador repetido voltam da API com o code e aparecem aqui.
 */
export function MoverCentroDialog({ centro, arvore, onFechar, onMovido }: MoverCentroProps) {
  const alvos = useMemo(() => alvosDeMover(centro, arvore), [centro, arvore]);
  const [destino, setDestino] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const mover = useMoverCentro();

  async function confirmar() {
    if (destino === '') return;
    setErro(null);
    try {
      const movido = await mover.mutateAsync({ id: centro.id, parentId: destino === RAIZ ? null : destino });
      onMovido(movido);
    } catch (e) {
      setErro(mensagemCentro(e));
    }
  }

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && onFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mover {rotuloCentro(centro)}</DialogTitle>
          <DialogDescription>
            O centro leva os filhos junto e os caminhos são recalculados. Só centros sintéticos ativos aparecem como destino; um centro não vai para dentro dele mesmo nem de um descendente.
          </DialogDescription>
        </DialogHeader>

        {alvos.length === 0 ? (
          <p role="status" className="text-small text-ink-body">
            Não há sintético de destino disponível. Crie um centro sintético antes.
          </p>
        ) : (
          <div>
            <Label htmlFor="mover-destino" className="mb-1.5 block text-caption font-medium text-ink-secondary">
              Novo pai
            </Label>
            <select
              id="mover-destino"
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-card px-3 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus"
            >
              <option value="">Escolha o destino</option>
              {alvos.map((a) => (
                <option key={a.id ?? RAIZ} value={a.id ?? RAIZ}>
                  {a.rotulo}
                </option>
              ))}
            </select>
          </div>
        )}

        {erro && (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">
            {erro}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onFechar}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => void confirmar()} disabled={destino === '' || mover.isPending}>
            {mover.isPending ? 'Movendo…' : 'Mover'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
